
import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
  UseFormReturn,
} from "react-hook-form"

import { cn } from "../../lib/utils"
import { Label } from "./label"

interface FormProps<T extends FieldValues = FieldValues>
  extends React.FormHTMLAttributes<HTMLFormElement> {
  form: UseFormReturn<T>;
}

const Form = <T extends FieldValues = FieldValues>({
  form,
  children,
  ...props
}: FormProps<T>) => {
  return (
    <FormProvider data-component-id="src/components/ui/form.tsx:29:4" data-component-path="src/components/ui/form.tsx" data-component-line="29" data-component-file="form.tsx" data-component-name="FormProvider" data-component-content="%7B%22elementName%22%3A%22FormProvider%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...form}>
      <form data-component-id="src/components/ui/form.tsx:30:6" data-component-path="src/components/ui/form.tsx" data-component-line="30" data-component-file="form.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...props}>{children}</form>
    </FormProvider>
  );
};

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue | undefined>(undefined)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider data-component-id="src/components/ui/form.tsx:51:4" data-component-path="src/components/ui/form.tsx" data-component-line="51" data-component-file="form.tsx" data-component-name="FormFieldContext.Provider" data-component-content="%7B%22elementName%22%3A%22FormFieldContext.Provider%22%7D" value={{ name: props.name }}>
      <Controller data-component-id="src/components/ui/form.tsx:52:6" data-component-path="src/components/ui/form.tsx" data-component-line="52" data-component-file="form.tsx" data-component-name="Controller" data-component-content="%7B%22elementName%22%3A%22Controller%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...props} />
    </FormFieldContext.Provider>
  )
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue | undefined>(undefined)

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  if (!fieldContext) {
    throw new Error("useFormField must be used within a <FormField>")
  }

  if (!itemContext) {
    throw new Error("useFormField must be used within a <FormItem>")
  }

  const fieldState = getFieldState(fieldContext.name, formState)
  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()

  return (
    <FormItemContext.Provider data-component-id="src/components/ui/form.tsx:96:4" data-component-path="src/components/ui/form.tsx" data-component-line="96" data-component-file="form.tsx" data-component-name="FormItemContext.Provider" data-component-content="%7B%22elementName%22%3A%22FormItemContext.Provider%22%7D" value={{ id }}>
      <div data-component-id="src/components/ui/form.tsx:97:6" data-component-path="src/components/ui/form.tsx" data-component-line="97" data-component-file="form.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = "FormItem"

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField()

  return (
    <Label data-component-id="src/components/ui/form.tsx:110:4" data-component-path="src/components/ui/form.tsx" data-component-line="110" data-component-file="form.tsx" data-component-name="Label" data-component-content="%7B%22elementName%22%3A%22Label%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormLabel.displayName = "FormLabel"

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot data-component-id="src/components/ui/form.tsx:127:4" data-component-path="src/components/ui/form.tsx" data-component-line="127" data-component-file="form.tsx" data-component-name="Slot" data-component-content="%7B%22elementName%22%3A%22Slot%22%2C%22id%22%3A%22%5Bvar%3AformItemId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
})
FormControl.displayName = "FormControl"

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <p data-component-id="src/components/ui/form.tsx:149:4" data-component-path="src/components/ui/form.tsx" data-component-line="149" data-component-file="form.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22id%22%3A%22%5Bvar%3AformDescriptionId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
FormDescription.displayName = "FormDescription"

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) return null

  return (
    <p data-component-id="src/components/ui/form.tsx:169:4" data-component-path="src/components/ui/form.tsx" data-component-line="169" data-component-file="form.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22id%22%3A%22%5Bvar%3AformMessageId%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = "FormMessage"

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
}
